import { Component, OnDestroy, effect, inject, input, output, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable } from 'rxjs';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { ImagePreviewDialogComponent } from '@shared/components/image-preview-dialog/image-preview-dialog.component';
import { ImagePaintDialogComponent } from '@shared/components/image-paint-dialog/image-paint-dialog.component';
import { AttachmentIntakeService } from '@shared/services/attachment-intake.service';

export interface FileAttachment {
  id: number;
  fileName: string;
  contentType?: string | null;
  fileSize?: number | null;
  createdAt?: string | Date | null;
}

export interface ImageEditedEvent {
  attachment: FileAttachment;
  /** PNG con las anotaciones pintadas encima. */
  blob: Blob;
}

/**
 * Lista genérica de archivos adjuntos. Las imágenes se muestran como
 * miniaturas previsualizables y editables (pintar encima); el resto,
 * como filas de archivo. El padre hace las llamadas HTTP: este componente
 * solo emite eventos, salvo la carga de miniaturas, que delega en el
 * `previewLoader` que le pase el padre.
 */
@Component({
  selector: 'app-file-attachment-list',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule, TranslatePipe],
  template: `
    <div class="attachment-list">
      @if (canEdit()) {
        <div class="attachment-actions">
          <input #fileInput type="file" multiple hidden (change)="onFilesSelected($event, fileInput)">
          <button mat-stroked-button type="button" (click)="fileInput.click()">
            <mat-icon>attach_file</mat-icon>
            {{ 'common.add' | translate }}
          </button>
          <button mat-stroked-button type="button"
                  [matTooltip]="'gdp.attachments.paste-hint' | translate"
                  (click)="paste()">
            <mat-icon>content_paste</mat-icon>
            {{ 'gdp.attachments.paste' | translate }}
          </button>
          <button mat-stroked-button type="button" (click)="draw()">
            <mat-icon>draw</mat-icon>
            {{ 'gdp.attachments.draw' | translate }}
          </button>
          <!-- Acciones extra que aporta el padre (p. ej. añadir diagrama) -->
          <ng-content select="[attachment-extra-action]" />
        </div>
      }

      <!-- Imágenes: miniaturas. Los diagramas del padre se proyectan en esta
           misma rejilla ([extra-thumbs]) para que archivos y diagramas fluyan
           en la misma fila (con wrap), no en filas separadas. -->
      @if (imageAttachments().length > 0 || hasExtraThumbs()) {
        <div class="thumb-grid">
          @for (attachment of imageAttachments(); track attachment.id) {
            <figure class="thumb" (click)="openPreview(attachment)">
              @if (urlOf(attachment.id); as url) {
                <img class="thumb-img" [src]="url" [alt]="attachment.fileName">
              } @else {
                <div class="thumb-loading"><mat-icon>image</mat-icon></div>
              }
              <figcaption class="thumb-caption">{{ attachment.fileName }}</figcaption>
              <span class="thumb-overlay" (click)="$event.stopPropagation()">
                <button mat-icon-button class="thumb-btn"
                        [matTooltip]="'gdp.attachments.preview' | translate"
                        (click)="openPreview(attachment)">
                  <mat-icon>zoom_in</mat-icon>
                </button>
                @if (canEdit()) {
                  <button mat-icon-button class="thumb-btn"
                          [matTooltip]="'gdp.attachments.edit-image' | translate"
                          (click)="openPaint(attachment)">
                    <mat-icon>brush</mat-icon>
                  </button>
                }
                <button mat-icon-button class="thumb-btn"
                        (click)="download.emit(attachment)">
                  <mat-icon>download</mat-icon>
                </button>
                @if (canEdit()) {
                  <button mat-icon-button class="thumb-btn thumb-btn-danger"
                          (click)="delete.emit(attachment)">
                    <mat-icon>delete</mat-icon>
                  </button>
                }
              </span>
            </figure>
          }
          <!-- Diagramas u otras miniaturas aportadas por el padre -->
          <ng-content select="[extra-thumbs]" />
        </div>
      }

      <!-- Resto de archivos: filas -->
      @for (attachment of otherAttachments(); track attachment.id) {
        <div class="file-row">
          <mat-icon class="file-icon">description</mat-icon>
          <span class="file-name">{{ attachment.fileName }}</span>
          <span class="file-meta">{{ getFileSizeLabel(attachment.fileSize) }}</span>
          <span class="file-spacer"></span>
          <button mat-icon-button type="button" (click)="download.emit(attachment)">
            <mat-icon>download</mat-icon>
          </button>
          @if (canEdit()) {
            <button mat-icon-button type="button" color="warn" (click)="delete.emit(attachment)">
              <mat-icon>delete</mat-icon>
            </button>
          }
        </div>
      }

      @if (showEmptyMessage() && attachments().length === 0) {
        <p class="attachment-empty">{{ 'common.no-data' | translate }}</p>
      }
    </div>
  `,
  styles: [`
    .attachment-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 100%;
    }
    .attachment-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }
    .thumb-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
      gap: 10px;
    }
    .thumb {
      position: relative;
      margin: 0;
      border: 1px solid var(--gdp-border, #e6e8f0);
      border-radius: 10px;
      overflow: hidden;
      cursor: zoom-in;
      background:
        repeating-conic-gradient(#f0f0f3 0% 25%, white 0% 50%) 0 0 / 16px 16px;
    }
    .thumb-img {
      display: block;
      width: 100%;
      height: 96px;
      object-fit: cover;
    }
    .thumb-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 96px;
      color: #c3c7d4;
    }
    .thumb-caption {
      font-size: 0.68rem;
      color: var(--gdp-muted, #6b7280);
      padding: 4px 8px;
      background: white;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .thumb-overlay {
      position: absolute;
      inset: 0 0 24px 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 2px;
      background: rgba(27, 20, 100, 0.55);
      opacity: 0;
      transition: opacity 0.15s ease;
    }
    .thumb:hover .thumb-overlay { opacity: 1; }
    .thumb-btn { color: white; width: 34px; height: 34px; padding: 5px; }
    .thumb-btn mat-icon { font-size: 19px; width: 19px; height: 19px; }
    .thumb-btn-danger { color: #ff8a80; }
    .file-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 4px 6px;
      border-radius: 8px;
      border: 1px solid var(--gdp-border, #e6e8f0);
    }
    .file-icon { color: var(--gdp-muted, #6b7280); }
    .file-name { font-size: 0.85rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .file-meta { font-size: 0.72rem; color: var(--gdp-muted, #999); white-space: nowrap; }
    .file-spacer { flex: 1 1 auto; }
    .attachment-empty {
      font-size: 0.85rem;
      color: var(--gdp-muted, #999);
      margin: 4px 0;
    }
  `]
})
export class FileAttachmentListComponent implements OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly intake = inject(AttachmentIntakeService);

  attachments = input.required<FileAttachment[]>();
  canEdit = input<boolean>(true);
  /** Permite suprimir el mensaje de vacío cuando el padre agrupa más listas. */
  showEmptyMessage = input<boolean>(true);
  /**
   * El padre proyecta miniaturas extra (p. ej. diagramas) en la rejilla via
   * `[extra-thumbs]`. Al activarlo, la rejilla se pinta aunque no haya imágenes,
   * para que esas miniaturas compartan fila con los adjuntos.
   */
  hasExtraThumbs = input<boolean>(false);
  /**
   * Descarga el binario de un adjunto (lo aporta el padre, que conoce el
   * endpoint). Sin él, las imágenes se listan como archivos normales.
   */
  previewLoader = input<((attachment: FileAttachment) => Observable<Blob>) | null>(null);

  upload = output<File[]>();
  download = output<FileAttachment>();
  delete = output<FileAttachment>();
  imageEdited = output<ImageEditedEvent>();

  private readonly objectUrls = signal<Record<number, string>>({});
  private readonly loading = new Set<number>();

  /**
   * Pila de instancias montadas: el evento paste global (Cmd/Ctrl+V) lo
   * atiende solo la lista editable más reciente (p. ej. el side sheet de
   * tarea por encima de la página).
   */
  private static readonly instances: FileAttachmentListComponent[] = [];
  private readonly onDocumentPaste = (event: ClipboardEvent) => this.handlePasteEvent(event);

  constructor() {
    FileAttachmentListComponent.instances.push(this);
    document.addEventListener('paste', this.onDocumentPaste);
    // Carga perezosa de miniaturas cuando cambia la lista de adjuntos
    effect(() => {
      const loader = this.previewLoader();
      if (!loader) {
        return;
      }
      for (const attachment of this.imageAttachments()) {
        if (this.objectUrls()[attachment.id] || this.loading.has(attachment.id)) {
          continue;
        }
        this.loading.add(attachment.id);
        untracked(() => loader(attachment).subscribe({
          next: blob => {
            this.loading.delete(attachment.id);
            this.objectUrls.update(urls => ({ ...urls, [attachment.id]: URL.createObjectURL(blob) }));
          },
          error: () => this.loading.delete(attachment.id)
        }));
      }
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('paste', this.onDocumentPaste);
    const index = FileAttachmentListComponent.instances.indexOf(this);
    if (index >= 0) {
      FileAttachmentListComponent.instances.splice(index, 1);
    }
    Object.values(this.objectUrls()).forEach(url => URL.revokeObjectURL(url));
  }

  async paste(): Promise<void> {
    const files = await this.intake.pasteFromClipboard();
    if (files.length > 0) {
      this.upload.emit(files);
    }
  }

  async draw(): Promise<void> {
    const files = await this.intake.draw();
    if (files.length > 0) {
      this.upload.emit(files);
    }
  }

  private async handlePasteEvent(event: ClipboardEvent): Promise<void> {
    if (!this.canEdit() || !this.isTopEditableInstance()) {
      return;
    }
    // No interceptar pegados dentro de campos de texto (inputs, quill…)
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, [contenteditable="true"], .ql-editor')) {
      return;
    }
    const files = await this.intake.handlePasteEvent(event);
    if (files.length > 0) {
      this.upload.emit(files);
    }
  }

  private isTopEditableInstance(): boolean {
    const editable = FileAttachmentListComponent.instances.filter(instance => instance.canEdit());
    return editable[editable.length - 1] === this;
  }

  isImage(attachment: FileAttachment): boolean {
    return (attachment.contentType ?? '').startsWith('image/');
  }

  imageAttachments(): FileAttachment[] {
    return this.previewLoader() ? this.attachments().filter(a => this.isImage(a)) : [];
  }

  otherAttachments(): FileAttachment[] {
    return this.previewLoader() ? this.attachments().filter(a => !this.isImage(a)) : this.attachments();
  }

  urlOf(attachmentId: number): string | null {
    return this.objectUrls()[attachmentId] ?? null;
  }

  /** Fuerza recargar la miniatura tras una edición guardada. */
  refreshThumbnail(attachmentId: number): void {
    const url = this.objectUrls()[attachmentId];
    if (url) {
      URL.revokeObjectURL(url);
      this.objectUrls.update(urls => {
        const next = { ...urls };
        delete next[attachmentId];
        return next;
      });
    }
  }

  openPreview(attachment: FileAttachment): void {
    const url = this.urlOf(attachment.id);
    if (!url) {
      return;
    }
    this.dialog.open(ImagePreviewDialogComponent, {
      data: { imageUrl: url, fileName: attachment.fileName },
      maxWidth: '92vw'
    });
  }

  openPaint(attachment: FileAttachment): void {
    const url = this.urlOf(attachment.id);
    if (!url) {
      return;
    }
    this.dialog.open(ImagePaintDialogComponent, {
      data: { imageUrl: url, fileName: attachment.fileName },
      maxWidth: '94vw',
      disableClose: true
    }).afterClosed().subscribe((blob?: Blob) => {
      if (blob) {
        this.imageEdited.emit({ attachment, blob });
      }
    });
  }

  onFilesSelected(event: Event, fileInput: HTMLInputElement): void {
    const files = Array.from((event.target as HTMLInputElement).files ?? []);
    if (files.length > 0) {
      this.upload.emit(files);
    }
    fileInput.value = '';
  }

  getFileSizeLabel(size?: number | null): string {
    if (!size || size <= 0) {
      return '';
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
}
